import React from "react";
import { ArrowRight } from "lucide-react";
import { SITE } from "../lib/site";

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#0E2238]"
    >
      {/* Background video */}
      <video
        data-testid="hero-bg-video"
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        preload="none"
        poster={SITE.heroVideo.poster}
        aria-hidden="true"
      >
        <source src="/hero-bg.webm" type="video/webm" />
        <source src="/hero-bg.mp4" type="video/mp4" />
      </video>

      {/* Layered overlays for legibility & brand mood */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-r from-[#0E2238]/90 via-[#0E2238]/55 to-[#0E2238]/30"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-t from-[#0E2238]/80 via-transparent to-[#0E2238]/30"
      />
      <div
        aria-hidden="true"
        className="film-grain absolute inset-0"
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-36 md:pt-44 pb-20 md:pb-28 min-h-[100svh] flex flex-col justify-end">
        <div className="grid grid-cols-12 gap-6 lg:gap-12 items-end">
          <div className="col-span-12 lg:col-span-9">
            <p
              data-testid="hero-eyebrow"
              className="label-eyebrow reveal-fade"
              style={{ color: "#CBA365" }}
            >
              Premium Corporate Catering · Bengaluru
            </p>

            <h1
              data-testid="hero-headline"
              className="font-serif text-[#FDFBF7] mt-6 text-[3.25rem] sm:text-6xl md:text-7xl lg:text-[8rem] leading-[0.95] tracking-tight reveal-up"
            >
              Corporate catering
              <span className="italic font-light text-[#CBA365]"> built for </span>
              Bangalore offices.
            </h1>

            <p
              data-testid="hero-sub"
              className="mt-8 max-w-xl text-[#FDFBF7]/80 text-base md:text-lg font-light leading-relaxed reveal-up delay-200"
            >
              From daily employee meals and office lunch programs to corporate events, packed meal boxes and pantry management — workplace food services designed for Bengaluru teams.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4 reveal-up delay-300">
              <a
                href="#contact"
                data-testid="hero-cta-quote"
                className="group inline-flex items-center gap-3 bg-[#CBA365] text-[#1E3A5F] px-8 py-4 text-xs uppercase tracking-[0.24em] hover:bg-[#FDFBF7] transition-colors"
              >
                Request a Quote
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <a
                href="#menu"
                data-testid="hero-cta-menu"
                className="inline-flex items-center gap-3 border border-[#FDFBF7]/40 text-[#FDFBF7] px-8 py-4 text-xs uppercase tracking-[0.24em] hover:bg-[#FDFBF7] hover:text-[#1E3A5F] transition-all duration-300"
              >
                Explore the Menu
              </a>
            </div>
          </div>
        </div>

        {/* Footer strip — stats + Since 2022 */}
        <div className="mt-16 md:mt-20 grid grid-cols-12 gap-6 items-end reveal-up delay-500">
          <div className="col-span-12 md:col-span-8 grid grid-cols-3 gap-6 max-w-2xl">
            {[
              { k: "3+", v: "Years Crafting" },
              { k: "60+", v: "Corporate Clients" },
              { k: "4.9★", v: "Average Rating" },
            ].map((s) => (
              <div
                key={s.v}
                className="border-t border-[#FDFBF7]/25 pt-4"
                data-testid={`hero-stat-${s.v.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="font-serif text-3xl md:text-4xl text-[#FDFBF7]">
                  {s.k}
                </div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#FDFBF7]/65 mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>

          <div className="hidden md:block col-span-4">
            <div className="ml-auto max-w-[260px] bg-[#FDFBF7]/10 backdrop-blur-md border border-[#CBA365]/40 text-[#FDFBF7] p-6 md:p-7">
              <div className="label-eyebrow" style={{ color: "#CBA365" }}>
                Since 2022
              </div>
              <p className="font-serif text-[#FDFBF7] text-xl md:text-[1.4rem] leading-snug mt-2">
                A quiet standard of excellence on every plate.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative numeral */}
      <div
        aria-hidden="true"
        className="hidden md:block absolute right-6 top-28 font-serif text-[10rem] lg:text-[14rem] leading-none text-[#FDFBF7]/10 pointer-events-none select-none z-10"
      >
        01
      </div>

      {/* Scroll indicator */}
      <div
        aria-hidden="true"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-[10px] uppercase tracking-[0.32em] text-[#FDFBF7]/60 font-sans-brand"
      >
        Scroll
      </div>
    </section>
  );
}
